"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Logo from "@/app/components/Logo";
import Button from "@/app/components/Button";
import type { EncyclopediaCount, EncyclopediaEntry, EncyclopediaResult } from "@/lib/master-encyclopedia";

const WORLD = "Baunheim";

/** 索引から外す置き場 */
const SKIP = ["unique/maps"];

/** 入力が止まってから引くまでの間 */
const SEARCH_WAIT_MS = 250;

const LAYER_ORDER = ["archetype", "catalog", "unique"];
const LAYER_LABEL: Record<string, string> = {
  archetype: "型の型",
  catalog: "型",
  unique: "固有",
};

const INITIAL_ORDER = ["あ", "か", "さ", "た", "な", "は", "ま", "や", "ら", "わ", "漢字", "英数"];

const KIND_ORDER = [
  "creatures",
  "enemies",
  "items",
  "characters",
  "groups",
  "places",
  "skills",
  "mastery",
  "modifiers",
  "materials",
  "races",
  "vehicles",
  "events",
  "rules",
  "phenomena",
  "others",
];
const KIND_LABEL: Record<string, string> = {
  creatures: "生物",
  enemies: "敵",
  items: "アイテム",
  characters: "キャラ",
  groups: "グループ",
  places: "場所",
  skills: "スキル",
  mastery: "体得",
  modifiers: "モディファイア",
  materials: "物質",
  races: "種族",
  vehicles: "乗り物",
  events: "イベント",
  rules: "ルール",
  phenomena: "事象・現象",
  others: "その他",
};

/** 分類の表記名。キーは「種別/分類」。ここに無い分類は索引の持つ表記名を使う */
const SUB_LABEL: Record<string, string> = {
  "creatures/animals": "動物",
  "creatures/plants": "植物",
  "creatures/aquatic": "魚介",
  "creatures/others": "その他",
  "enemies/monsters": "魔物",
  "enemies/creatures": "生き物",
  "enemies/people": "人の敵",
  "enemies/scenario": "シナリオ専用",
  "characters/pc": "PC",
  "characters/npc": "NPC",
  "groups/nations": "国家",
  "groups/states": "州",
  "groups/towns": "街",
  "groups/organizations": "組織",
  "places/natural": "自然",
  "places/ruins": "遺構",
  "places/lairs": "巣",
  "places/roads": "街道",
  "rules/principles": "原理",
  "rules/techniques": "技術",
  "rules/institutions": "制度",
  "rules/faiths": "信仰",
  "rules/laws": "法律",
  "rules/cultures": "文化",
  "phenomena/weather": "天候",
  "phenomena/regional-environment": "地域環境",
  "phenomena/history": "歴史",
  "phenomena/scenarios": "シナリオ",
  "others/multi-phenomena": "複数事象",
  "items/goods": "実体になる型",
  "items/abstract": "通貨",
  "vehicles/land": "陸",
  "vehicles/water": "水",
  "vehicles/air": "空",
};

interface FolderEntry {
  name: string;
  dir: boolean;
}

/** 画面の段。種別を選ぶ・分類を選ぶ・一覧を見る */
type Step = "kinds" | "subs" | "list";

const kindLabel = (kind: string) => KIND_LABEL[kind] ?? kind;

function subLabel(kind: string, sub: string, labels: Record<string, string>): string {
  if (sub === "") return "分類なし";
  return SUB_LABEL[`${kind}/${sub}`] ?? labels[`${kind}/${sub}`] ?? labels[`skills/${sub}`] ?? sub;
}

const folderOf = (entry: EncyclopediaEntry) => [entry.layer, entry.kind, entry.sub, entry.slug].filter((x) => x !== "").join("/");

function ordered(counts: EncyclopediaCount[], order: string[]): EncyclopediaCount[] {
  const rank = (key: string) => {
    const i = order.indexOf(key);
    return i < 0 ? order.length : i;
  };
  return [...counts].sort((a, b) => rank(a.key) - rank(b.key) || a.key.localeCompare(b.key));
}

function stampOf(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const chipsStyle: React.CSSProperties = { display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 };

const chipStyle = (on: boolean): React.CSSProperties => ({
  minHeight: 36,
  padding: "6px 10px",
  fontSize: 13,
  fontFamily: "inherit",
  color: on ? "#1a1509" : "var(--text)",
  background: on ? "var(--accent)" : "var(--bg-field)",
  fontWeight: on ? 700 : 400,
  border: `1px solid ${on ? "var(--accent)" : "var(--line)"}`,
  borderRadius: 6,
  cursor: "pointer",
});

const tilesStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
  gap: 8,
  marginBottom: 18,
};

const tileStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 2,
  minHeight: 60,
  padding: "10px 12px",
  fontFamily: "inherit",
  textAlign: "left",
  color: "var(--text)",
  background: "var(--bg-field)",
  border: "1px solid var(--line)",
  borderRadius: 6,
  cursor: "pointer",
};

const preStyle: React.CSSProperties = {
  fontSize: 12.5,
  lineHeight: 1.6,
  fontFamily: '"BIZ UDGothic", "MS Gothic", Consolas, monospace',
  whiteSpace: "pre-wrap",
  overflowWrap: "anywhere",
  background: "var(--bg-field)",
  border: "1px solid var(--line)",
  borderRadius: 6,
  padding: "10px 12px",
  margin: "0 0 8px",
};

/** 札の並び。押した札をもう一度押すと外す */
function Chips({
  items,
  value,
  label,
  onPick,
}: {
  items: EncyclopediaCount[];
  value: string | null;
  label: (key: string) => string;
  onPick: (key: string | null) => void;
}) {
  return (
    <div style={chipsStyle}>
      {items.map((x) => (
        <button key={x.key} style={chipStyle(value === x.key)} onClick={() => onPick(value === x.key ? null : x.key)}>
          {label(x.key)} {x.count.toLocaleString()}
        </button>
      ))}
    </div>
  );
}

/** 種別と分類を選ぶ大きなボタン */
function Tile({ name, count, onClick }: { name: string; count: number; onClick: () => void }) {
  return (
    <button style={tileStyle} onClick={onClick}>
      <span style={{ fontSize: 15, color: "var(--accent-strong)" }}>{name}</span>
      <span style={{ fontSize: 12, color: "var(--text-dim)" }}>{count.toLocaleString()} 件</span>
    </button>
  );
}

/** md の本文を見出し・箇条書き・段落に組む */
function Prose({ text }: { text: string }) {
  const blocks: React.ReactNode[] = [];
  let list: string[] = [];
  let code: string[] | null = null;

  const flush = () => {
    if (list.length === 0) return;
    blocks.push(
      <ul className="review-list" key={blocks.length}>
        {list.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
    );
    list = [];
  };

  for (const raw of text.split("\n")) {
    if (raw.trim().startsWith("```")) {
      if (code === null) {
        flush();
        code = [];
      } else {
        blocks.push(
          <pre style={preStyle} key={blocks.length}>
            {code.join("\n")}
          </pre>
        );
        code = null;
      }
      continue;
    }
    if (code !== null) {
      code.push(raw);
      continue;
    }

    const line = raw.trim();
    const item = /^[-*]\s+(.*)$/.exec(line);
    if (item) {
      list.push(item[1]);
      continue;
    }
    flush();
    if (line === "" || line.startsWith("# ")) continue;

    const head = /^(#{2,6})\s+(.*)$/.exec(line);
    if (head) {
      blocks.push(
        <h3 className={head[1].length === 2 ? "review-h3" : "review-h4"} key={blocks.length}>
          {head[2]}
        </h3>
      );
      continue;
    }
    blocks.push(
      <p className="review-p" key={blocks.length}>
        {line}
      </p>
    );
  }
  flush();
  if (code !== null) {
    blocks.push(
      <pre style={preStyle} key={blocks.length}>
        {code.join("\n")}
      </pre>
    );
  }
  return <>{blocks}</>;
}

/** 情報 1 件の画面。メインの md と、情報フォルダの中のファイルを見せる */
function EntryView({ entry, labels, onBack }: { entry: EncyclopediaEntry; labels: Record<string, string>; onBack: () => void }) {
  const home = folderOf(entry);
  const main = `${home}/${entry.file}`;

  const [dir, setDir] = useState(home);
  const [items, setItems] = useState<FolderEntry[]>([]);
  const [path, setPath] = useState(main);
  const [text, setText] = useState("");
  const [message, setMessage] = useState("読み込み中・・・");

  useEffect(() => {
    if (entry.slug === "") return;
    let alive = true;
    fetch(`/api/admin/master/list?world=${WORLD}&path=${encodeURIComponent(dir)}`)
      .then(async (response) => {
        const body = await response.json();
        if (!alive) return;
        if (!response.ok) {
          setMessage(body.error ?? "フォルダを読めません");
          return;
        }
        setItems(body.entries as FolderEntry[]);
      })
      .catch(() => alive && setMessage("サーバーに繋がりません"));
    return () => {
      alive = false;
    };
  }, [dir, entry.slug]);

  useEffect(() => {
    let alive = true;
    fetch(`/api/admin/master/file?world=${WORLD}&path=${encodeURIComponent(path)}`)
      .then(async (response) => {
        const body = await response.json();
        if (!alive) return;
        if (!response.ok) {
          setText("");
          setMessage(body.error ?? "ファイルを読めません");
          return;
        }
        setText(body.text as string);
        setMessage("");
      })
      .catch(() => alive && setMessage("サーバーに繋がりません"));
    return () => {
      alive = false;
    };
  }, [path]);

  const place = [kindLabel(entry.kind)];
  if (entry.sub !== "") place.push(subLabel(entry.kind, entry.sub, labels));
  place.push(LAYER_LABEL[entry.layer] ?? entry.layer);

  return (
    <div className="panel panel-wide">
      <Logo />
      <div className="head">
        <span className="head-user">
          {place.join("／")}
          <strong>{entry.name}</strong>
        </span>
        <Button onClick={onBack} inline>
          戻る
        </Button>
      </div>

      <p className="review-dim" style={{ overflowWrap: "anywhere", marginBottom: 10 }}>
        {home}
      </p>

      {entry.slug !== "" ? (
        <div style={chipsStyle}>
          {dir !== home ? (
            <button style={chipStyle(false)} onClick={() => setDir(dir.slice(0, dir.lastIndexOf("/")))}>
              ↑ {dir.slice(home.length + 1)}
            </button>
          ) : null}
          {items.map((item) => {
            const child = `${dir}/${item.name}`;
            return item.dir ? (
              <button key={child} style={chipStyle(false)} onClick={() => setDir(child)}>
                ▸ {item.name}
              </button>
            ) : (
              <button key={child} style={chipStyle(path === child)} onClick={() => setPath(child)}>
                {item.name}
              </button>
            );
          })}
        </div>
      ) : null}

      <section className="section">
        <h2 className="section-title" style={{ overflowWrap: "anywhere" }}>
          {path.slice(home.length + 1) || entry.file}
        </h2>
        {path.endsWith(".md") ? <Prose text={text} /> : <pre style={preStyle}>{text}</pre>}
      </section>

      <p className="message message-info">{message}</p>
    </div>
  );
}

/** Baunheim の master を種別・分類の順に選び、表記名の順の一覧から探す図鑑。管理者用画面 */
export default function Encyclopedia({ onBack }: { onBack: () => void }) {
  const [step, setStep] = useState<Step>("kinds");
  const [kind, setKind] = useState<string | null>(null);
  const [sub, setSub] = useState<string | null>(null);
  /** 選んだ種別が分類を持つか。一覧から戻る先を決める */
  const [hasSubs, setHasSubs] = useState(false);

  const [q, setQ] = useState("");
  const [wholeQ, setWholeQ] = useState("");
  const [layer, setLayer] = useState<string | null>(null);
  const [initial, setInitial] = useState<string | null>(null);

  const [result, setResult] = useState<EncyclopediaResult | null>(null);
  /** result がどの段の結果か */
  const [shown, setShown] = useState("");
  const [rows, setRows] = useState<EncyclopediaEntry[]>([]);
  const [message, setMessage] = useState("読み込み中・・・");
  const [busy, setBusy] = useState(false);
  const [opened, setOpened] = useState<EncyclopediaEntry | null>(null);
  const turn = useRef(0);

  const stage = `${step}/${kind ?? ""}/${sub ?? "*"}`;

  const search = useCallback(
    async (offset: number) => {
      const mine = ++turn.current;
      const listing = step === "list";
      const params = new URLSearchParams({
        world: WORLD,
        kind: kind ?? "",
        q: listing ? q : "",
        layer: listing ? layer ?? "" : "",
        initial: listing ? initial ?? "" : "",
        offset: String(offset),
      });
      // 分類の「分類なし」は空文字で送れないため「.」で送る
      if (listing && sub !== null) params.set("sub", sub === "" ? "." : sub);
      for (const item of SKIP) params.append("skip", item);
      try {
        const response = await fetch(`/api/admin/encyclopedia?${params}`);
        const body = await response.json();
        if (mine !== turn.current) return;
        if (!response.ok) {
          setMessage(body.error ?? "図鑑を読めません");
          return;
        }
        const next = body as EncyclopediaResult;
        // 分類を持たない種別は、分類を選ぶ段を飛ばして一覧へ進む
        if (step === "subs" && !next.subs.some((x) => x.key !== "")) {
          setHasSubs(false);
          setStep("list");
          return;
        }
        setResult(next);
        setShown(stage);
        setRows((previous) => (offset === 0 ? next.rows : [...previous, ...next.rows]));
        setMessage("");
      } catch {
        if (mine === turn.current) setMessage("サーバーに繋がりません");
      }
    },
    [step, kind, sub, q, layer, initial, stage]
  );

  useEffect(() => {
    const timer = setTimeout(() => search(0), step === "list" ? SEARCH_WAIT_MS : 0);
    return () => clearTimeout(timer);
  }, [search, step]);

  const rebuild = async () => {
    if (busy) return;
    setBusy(true);
    setMessage("索引を作っています");
    try {
      const response = await fetch("/api/admin/encyclopedia", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ world: WORLD, skip: SKIP }),
      });
      const body = await response.json();
      if (!response.ok) {
        setMessage(body.error ?? "索引を作れません");
        return;
      }
      await search(0);
      setMessage("索引を作り直しました");
    } catch {
      setMessage("サーバーに繋がりません");
    } finally {
      setBusy(false);
    }
  };

  /** 一覧の段へ進む。一覧の絞り込みは空から始める */
  const toList = (nextKind: string | null, nextSub: string | null, word: string) => {
    setKind(nextKind);
    setSub(nextSub);
    setQ(word);
    setLayer(null);
    setInitial(null);
    setStep("list");
  };

  const back = () => {
    if (step === "kinds") {
      onBack();
    } else if (step === "list" && kind !== null && hasSubs) {
      setSub(null);
      setStep("subs");
    } else {
      setKind(null);
      setSub(null);
      setStep("kinds");
    }
  };

  if (opened) {
    return <EntryView entry={opened} labels={result?.subLabels ?? {}} onBack={() => setOpened(null)} />;
  }

  const labels = result?.subLabels ?? {};
  // 今の段の結果だけを画面に出す
  const data = shown === stage ? result : null;
  const collator = new Intl.Collator("ja");

  const title =
    step === "kinds"
      ? "図鑑"
      : kind === null
        ? "全体から検索"
        : step === "list" && sub !== null
          ? `${kindLabel(kind)}・${subLabel(kind, sub, labels)}`
          : kindLabel(kind);

  return (
    <div className="panel panel-wide">
      <Logo />
      <div className="head">
        <span className="head-user">
          {step === "kinds" ? WORLD : "図鑑"}
          <strong>{title}</strong>
        </span>
        <Button onClick={back} inline>
          戻る
        </Button>
      </div>

      {data && step === "kinds" ? (
        <>
          <div style={tilesStyle}>
            {ordered(data.kinds, KIND_ORDER).map((x) => (
              <Tile
                key={x.key}
                name={kindLabel(x.key)}
                count={x.count}
                onClick={() => {
                  setKind(x.key);
                  setSub(null);
                  setHasSubs(true);
                  setStep("subs");
                }}
              />
            ))}
          </div>

          <form
            style={{ display: "flex", gap: 8, marginBottom: 18 }}
            onSubmit={(e) => {
              e.preventDefault();
              if (wholeQ.trim() !== "") toList(null, null, wholeQ);
            }}
          >
            <input
              className="field-input"
              value={wholeQ}
              onChange={(e) => setWholeQ(e.target.value)}
              placeholder="全体から探す"
              style={{ flex: 1, minWidth: 0 }}
            />
            <Button type="submit" inline>
              探す
            </Button>
          </form>

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <span className="review-dim">
              索引 {data.count.toLocaleString()} 件 ／ {stampOf(data.builtAt)} 作成
            </span>
            <Button onClick={rebuild} disabled={busy} inline>
              索引を作り直す
            </Button>
          </div>
        </>
      ) : null}

      {data && step === "subs" && kind !== null ? (
        <div style={tilesStyle}>
          <Tile name="すべて" count={data.subs.reduce((sum, x) => sum + x.count, 0)} onClick={() => toList(kind, null, "")} />
          {[...data.subs]
            .sort((a, b) => collator.compare(subLabel(kind, a.key, labels), subLabel(kind, b.key, labels)))
            .map((x) => (
              <Tile key={x.key} name={subLabel(kind, x.key, labels)} count={x.count} onClick={() => toList(kind, x.key, "")} />
            ))}
        </div>
      ) : null}

      {step === "list" ? (
        <>
          <input
            className="field-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="名・スラッグ・要約・分類の語で絞る"
            style={{ marginBottom: 12 }}
          />

          {data ? (
            <>
              <Chips items={ordered(data.initials, INITIAL_ORDER)} value={initial} label={(key) => key} onPick={setInitial} />
              {data.layers.length > 1 || layer !== null ? (
                <Chips
                  items={ordered(data.layers, LAYER_ORDER)}
                  value={layer}
                  label={(key) => LAYER_LABEL[key] ?? key}
                  onPick={setLayer}
                />
              ) : null}

              <section className="section">
                <h2 className="section-title">
                  {initial ?? "あいうえお順"}
                  <span className="section-count">{data.total.toLocaleString()} 件</span>
                </h2>
                <div className="slots">
                  {rows.length === 0 ? <p className="section-note">当たる情報はありません。</p> : null}
                  {rows.map((row) => (
                    <div
                      className="slot"
                      key={`${folderOf(row)}/${row.file}`}
                      role="button"
                      tabIndex={0}
                      style={{ cursor: "pointer" }}
                      onClick={() => setOpened(row)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") setOpened(row);
                      }}
                    >
                      <div className="slot-body">
                        <div className="slot-name">{row.name}</div>
                        {row.summary !== "" ? <div className="slot-meta">{row.summary}</div> : null}
                        <div className="slot-meta" style={{ overflowWrap: "anywhere" }}>
                          {kind === null || sub === null ? (
                            <span className="slot-state">
                              {kind === null ? kindLabel(row.kind) : ""}
                              {kind === null && row.sub !== "" ? "・" : ""}
                              {row.sub !== "" ? subLabel(row.kind, row.sub, labels) : ""}{" "}
                            </span>
                          ) : null}
                          {LAYER_LABEL[row.layer] ?? row.layer}
                          {row.slug !== "" ? ` ／ ${row.slug}` : ""}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {rows.length < data.total ? (
                  <div className="menu">
                    <Button onClick={() => search(rows.length)}>さらに表示（残り {(data.total - rows.length).toLocaleString()} 件）</Button>
                  </div>
                ) : null}
              </section>
            </>
          ) : null}
        </>
      ) : null}

      <p className="message message-info">{message}</p>
    </div>
  );
}
